import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, BookOpen, PenLine, Bot, CalendarDays, LogOut, BarChart2, ClipboardCheck, KeyRound } from "lucide-react";

export default function PortalShell({ children, title, subtitle, role = "student" }) {
  const { user, logout } = useAuth();
  const loc = useLocation();

  const studentLinks = [
    { to: "/student", label: "Overview", icon: <LayoutDashboard size={16}/> },
    { to: "/student/questions", label: "Question Bank", icon: <BookOpen size={16}/>, off: true },
    { to: "/student/assignments", label: "My Assignments", icon: <ClipboardCheck size={16}/> },
    { to: "/student/quiz", label: "Practice Quiz", icon: <PenLine size={16}/>, off: true },
    // Off because it bills the practice's own Anthropic API key per message,
    // and nobody had decided whether to pay for it. Not a technical problem:
    // delete the flag and it is back.
    { to: "/student/tutor", label: "AI Maths Tutor", icon: <Bot size={16}/>, off: true },
    { to: "/student/book", label: "Book a Lesson", icon: <CalendarDays size={16}/>, off: true },
    { to: "/student/analytics", label: "My Analytics", icon: <BarChart2 size={16}/> },
  ];
  const parentLinks = [
    { to: "/parent", label: "Child Progress", icon: <LayoutDashboard size={16}/> },
  ];
  /**
   * `off` hides a page from the menu until the tutor wants it.
   *
   * The whole list is kept, rather than the hidden entries being deleted,
   * because turning one back on should be one word - and because a deleted
   * line takes its icon, its label and the fact that the page exists at all
   * with it. `STUDENT_PAGES_OFF` in App.js blocks the routes to match; a menu
   * that merely stops *mentioning* a page is not hidden, it is unlisted, and
   * a child who has bookmarked it carries on as before.
   */
  const links = (role === "parent" ? parentLinks : studentLinks).filter((l) => !l.off);

  return (
    <div className="min-h-screen bg-slate-50 grid md:grid-cols-[260px_1fr]">
      <aside className="bg-brand-navy text-white p-6 flex flex-col md:min-h-screen">
        <Link to="/" className="flex items-center gap-3 mb-10" data-testid="portal-logo">
          <img src="/brand/logo.png" alt="Genius Education" className="h-10 w-auto object-contain" style={{maxWidth: 160}}/>
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 -mt-8 mb-10">{role === "parent" ? "Parent Portal" : "Student Portal"}</div>

        <nav className="space-y-1">
          {links.map(l => (
            <Link key={l.to} to={l.to}
              data-testid={`sidebar-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${loc.pathname === l.to ? 'bg-white/10 brand-gold' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}>
              {l.icon} {l.label}
            </Link>
          ))}
        </nav>

        <div className="mt-auto pt-8 border-t border-white/10">
          <div className="text-xs text-white/60 mb-3">
            <div className="font-medium text-white/90">{user?.name}</div>
            <div className="capitalize">{user?.role} · {user?.branch || 'Wokingham'}</div>
          </div>
          <Link to="/password" className="flex items-center gap-2 text-xs text-white/60 hover:text-white mb-2">
            <KeyRound size={14}/> Change my password
          </Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-white/60 hover:text-white" data-testid="portal-logout">
            <LogOut size={14}/> Sign out
          </button>
        </div>
      </aside>

      <main className="p-6 md:p-10 max-w-6xl w-full">
        {/* Only on the demo logins. This used to be on every page of every
            account, so a child the tutor had enrolled, with real homework
            waiting, was told their portal was a glimpse of a future one that
            activates after a trial lesson they had already had. */}
        {user?.preview_only && (
        <div className="mb-6 rounded-lg border border-brand-gold/40 bg-brand-gold/10 px-4 py-3 text-sm brand-navy flex items-start gap-3" data-testid="demo-banner">
          <span className="inline-block w-2 h-2 rounded-full bg-brand-gold mt-1.5 shrink-0 animate-pulse"></span>
          <div className="leading-snug">
            <strong className="font-semibold">Preview portal</strong> — a glimpse of your future {role === "parent" ? "parent" : "student"} dashboard. Your <strong>real portal with full progress &amp; live classwork</strong> activates after your first trial lesson with us.
          </div>
        </div>
        )}
        <div className="mb-8">
          <h1 className="font-serif text-3xl md:text-4xl tracking-tighter-x brand-navy">{title}</h1>
          {subtitle && <p className="text-slate-600 mt-2">{subtitle}</p>}
        </div>
        {children}
      </main>
    </div>
  );
}
